/**
 * Voxly — PinchZoomImage.
 *
 * Wraps a single `<img>` in a pointer-driven pinch-to-zoom / pan container.
 * Vanilla pointer events (no external deps). Supports:
 *   - Two-finger pinch → zoom, anchored on the pinch midpoint
 *   - One-finger drag when zoomed
 *   - Double-tap to reset (or zoom-in when at 1x)
 *   - Zoom range: 1× — 4×
 *
 * The parent gets `data-testid="pinch-zoom"` so tests can address the wrapper
 * and `data-testid="pinch-zoom-image"` on the actual `<img>`.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";

const MIN_SCALE = 1;
const MAX_SCALE = 4;

// Distance between two active pointer events (used to compute pinch scale).
function distance(a, b) {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy);
}

// Center point of two pointers.
function midpoint(a, b) {
  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
}

export default function PinchZoomImage({ src, alt = "", style, className = "", testid = "pinch-zoom" }) {
  const containerRef = useRef(null);
  const pointersRef = useRef(new Map());
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  // Refs mirror state so the pointer handlers (which close over stale
  // values) can read the latest transform for delta math.
  const scaleRef = useRef(1);
  const txRef = useRef(0);
  const tyRef = useRef(0);
  useEffect(() => { scaleRef.current = scale; }, [scale]);
  useEffect(() => { txRef.current = tx; }, [tx]);
  useEffect(() => { tyRef.current = ty; }, [ty]);

  // Gesture start state — captured on the first two-pointer or first
  // one-pointer down, and consumed on move.
  const gestureRef = useRef(null);
  const lastTapRef = useRef(0);

  const resetTransform = useCallback(() => {
    setScale(1); setTx(0); setTy(0);
  }, []);

  // Clamp pan so the image doesn't drift entirely off-screen at any scale.
  const clamp = useCallback((nx, ny, nScale) => {
    const c = containerRef.current;
    if (!c) return { x: nx, y: ny };
    const rect = c.getBoundingClientRect();
    const maxX = (rect.width * (nScale - 1)) / 2;
    const maxY = (rect.height * (nScale - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, nx)),
      y: Math.max(-maxY, Math.min(maxY, ny)),
    };
  }, []);

  const onPointerDown = useCallback((e) => {
    // Only track primary pointer buttons; ignore right-click / stylus barrel.
    if (e.button !== undefined && e.button > 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointersRef.current.set(e.pointerId, e);
    // Two-finger pinch — capture initial midpoint + distance.
    if (pointersRef.current.size === 2) {
      const [p1, p2] = Array.from(pointersRef.current.values());
      gestureRef.current = {
        type: "pinch",
        startDistance: distance(p1, p2),
        startMidpoint: midpoint(p1, p2),
        startScale: scaleRef.current,
        startTx: txRef.current,
        startTy: tyRef.current,
      };
    } else if (pointersRef.current.size === 1) {
      // Double-tap detection: two taps within 300 ms → toggle 1× ↔ 2×.
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        if (scaleRef.current > 1) resetTransform();
        else setScale(2);
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
      gestureRef.current = {
        type: "pan",
        startX: e.clientX,
        startY: e.clientY,
        startTx: txRef.current,
        startTy: tyRef.current,
      };
    }
  }, [resetTransform]);

  const onPointerMove = useCallback((e) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, e);
    const g = gestureRef.current;
    if (!g) return;
    if (g.type === "pinch" && pointersRef.current.size === 2) {
      const [p1, p2] = Array.from(pointersRef.current.values());
      const d = distance(p1, p2);
      let nScale = g.startScale * (d / g.startDistance);
      nScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, nScale));
      // Anchor zoom on the pinch start midpoint so the image doesn't
      // drift when pinching — mimicking iOS Photos.
      const c = containerRef.current;
      const rect = c ? c.getBoundingClientRect() : null;
      if (rect) {
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const focalDx = g.startMidpoint.x - cx;
        const focalDy = g.startMidpoint.y - cy;
        const scaleDelta = nScale / g.startScale;
        const clamped = clamp(
          g.startTx - focalDx * (scaleDelta - 1),
          g.startTy - focalDy * (scaleDelta - 1),
          nScale,
        );
        setScale(nScale);
        setTx(clamped.x);
        setTy(clamped.y);
      } else {
        setScale(nScale);
      }
    } else if (g.type === "pan" && pointersRef.current.size === 1 && scaleRef.current > 1) {
      const dx = e.clientX - g.startX;
      const dy = e.clientY - g.startY;
      const clamped = clamp(g.startTx + dx, g.startTy + dy, scaleRef.current);
      setTx(clamped.x);
      setTy(clamped.y);
    }
  }, [clamp]);

  const onPointerUp = useCallback((e) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size === 0) gestureRef.current = null;
  }, []);

  return (
    <div
      ref={containerRef}
      data-testid={testid}
      className={`relative w-full h-full flex items-center justify-center overflow-hidden touch-none select-none ${className}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        data-testid={`${testid}-image`}
        className="max-w-full max-h-full w-auto h-auto object-contain will-change-transform"
        style={{
          transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
          transition: gestureRef.current ? "none" : "transform 180ms ease-out",
          ...style,
        }}
      />
      {scale > 1 && (
        <button
          onClick={resetTransform}
          data-testid={`${testid}-reset`}
          className="absolute top-4 right-4 h-9 px-3 rounded-full bg-white/10 backdrop-blur-md text-white text-xs font-bold tap-press"
        >
          Reset zoom
        </button>
      )}
    </div>
  );
}
